import { NavLink, Outlet } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchHealth, fetchMe, fetchMeta, logout } from '../api';

/** Navegación. `ready: false` marca lo que aún no está implementado. */
const NAV = [
  { to: '/', label: 'Dashboard', ready: true },
  { to: '/projects', label: 'Proyectos', ready: true },
  { to: '/prs', label: 'Pull requests', ready: true },
  { to: '/ci', label: 'CI/CD', ready: true },
  { to: '/activity', label: 'Actividad', ready: true },
  { to: '/tasks', label: 'Tareas', ready: true },
  { to: '/docs', label: 'Documentación', ready: true },
  { to: '/risks', label: 'Riesgos', ready: true },
  { to: '/status', label: 'Sistema', ready: true },
  { to: '/conexiones', label: 'Conexiones', ready: true },
  { to: '/admin', label: 'Admin', ready: true },
] as const;

export function Layout(): JSX.Element {
  const client = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, retry: false, staleTime: 300_000 });
  const meta = useQuery({ queryKey: ['meta'], queryFn: fetchMeta, staleTime: 300_000 });
  const health = useQuery({ queryKey: ['health'], queryFn: fetchHealth, refetchInterval: 120_000 });

  const signOut = useMutation({
    mutationFn: logout,
    onSuccess: async () => {
      await client.invalidateQueries();
      window.location.assign('/login');
    },
  });

  const degraded = meta.data && !meta.data.github.configured;
  const isBootstrap = me.data?.login === 'bootstrap';

  return (
    <div className="min-h-screen">
      <header className="border-b border-[var(--color-border-subtle)] bg-[var(--color-surface-muted)]">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-x-6 gap-y-2 px-6 py-3">
          <span className="font-semibold tracking-tight">Software Command Center</span>
          <nav className="flex flex-wrap gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  [
                    'rounded px-2.5 py-1 text-[13px]',
                    isActive
                      ? 'bg-[var(--color-surface)] font-medium text-[var(--color-ink)]'
                      : 'text-[var(--color-ink-muted)] hover:text-[var(--color-ink)]',
                  ].join(' ')
                }
              >
                {item.label}
                {!item.ready && (
                  <span className="ml-1 opacity-50" title="Pendiente de implementar">
                    ·
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3 text-[12px] text-[var(--color-ink-muted)]">
            <span title={health.data ? `versión ${health.data.version}` : undefined}>
              {health.data ? (
                <>
                  {health.data.status === 'ok' ? '● ' : '▲ '}
                  {health.data.status === 'ok' ? 'operativo' : 'degradado'}
                </>
              ) : (
                'comprobando…'
              )}
            </span>
            {me.data && (
              <>
                <span className="text-[var(--color-ink)]">
                  {me.data.name}
                  <span className="ml-1 opacity-60">
                    ({me.data.role === 'admin' ? 'admin' : 'observador'})
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => signOut.mutate()}
                  className="underline hover:text-[var(--color-ink)]"
                >
                  salir
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {isBootstrap && (
        <div className="border-b border-amber-300 bg-amber-50 px-6 py-2 text-[12px] text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
          <strong className="font-semibold">Sesión de arranque con rol de administrador.</strong> Es
          un acceso temporal: configura OAuth con GitHub y vacía <code>SCC_BOOTSTRAP_TOKEN</code>.
        </div>
      )}

      {degraded && (
        <div className="border-b border-amber-300 bg-amber-50 px-6 py-2 text-[13px] text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
          <strong className="font-semibold">Datos de GitHub no disponibles.</strong>{' '}
          {meta.data?.github.reason} Las pantallas mostrarán «no disponible» en lugar de valores a
          cero hasta que se configure la credencial.
        </div>
      )}

      <main className="mx-auto max-w-[1400px] px-6 py-6">
        <Outlet />
      </main>
    </div>
  );
}
